import { gql, TypedDocumentNode } from '@apollo/client';

import {
  AssetRequest,
  AssetRequestCreateInput,
  AssetRequestsConnection,
  AssetRequestsConnectionInput,
  AssetRequestUpdateInput,
  ReplenishmentCreateInput,
  ReplenishmentUpdateInput,
} from '@atom/types/assetRequest';
import { showAdotInventoryRequests } from '@atom/utilities/featureToggleUtilities';

const ADOT_ASSET_REQUEST_SELECTION = showAdotInventoryRequests
  ? `assignedToGroups {
      id
      name
      colorId
    }
    purchaseUnit {
      unit
      conversionNumber
      baseUnit
    }
    purchaseOrderNumber`
  : '';

export const BASE_ASSET_REQUEST = gql`
  fragment BaseAssetRequest on AssetRequest {
    id
    type
    name
    assetId
    schemaName
    comments
    status
    createdDate
    createdBy {
      id
      firstName
      lastName
    }
    updatedDate
    updatedBy {
      id
      firstName
      lastName
    }

    # ARDOT
    attributes {
      id
      name
      oldValue
      newValue
    }
    categoryId
    categoryPath
    toCategoryId
    toCategoryPath
    reviewer {
      id
      firstName
      lastName
    }
    receiver {
      id
      firstName
      lastName
    }
    reviewDate
    reviewBy {
      id
      firstName
      lastName
    }
    receiveDate
    receiveBy {
      id
      firstName
      lastName
    }
    cancelDate
    resolutionDate

    # SDDOT
    assetName
    assetUnit
    quantity
    quantityOrdered
    quantityApproved
    reason
    assignedTo {
      id
      firstName
      lastName
      photoUrl
    }
    fromAssetId
    fromAssetName
    replenishments {
      id
      name
      quantityReceived
      unitCost
      totalCost
      invoiceNumber
      comments
      dateReceived
      createdDate
      createdBy {
        id
        firstName
        lastName
      }
      updatedDate
      updatedBy {
        id
        firstName
        lastName
      }
    }

    # ADOT
    ${ADOT_ASSET_REQUEST_SELECTION}
  }
`;

export const GET_ASSET_REQUEST: TypedDocumentNode<
  { assetRequest: AssetRequest },
  { id: string }
> = gql`
  query assetRequest($id: ID!) {
    assetRequest(id: $id) {
      ...BaseAssetRequest
    }
  }
  ${BASE_ASSET_REQUEST}
`;

export const GET_ASSET_REQUESTS: TypedDocumentNode<
  { assetRequests: AssetRequestsConnection },
  { input: AssetRequestsConnectionInput }
> = gql`
  query assetRequests($input: AssetRequestsConnectionInput!) {
    assetRequests(input: $input) {
      totalCount
      assetRequests {
        ...BaseAssetRequest
      }
    }
  }
  ${BASE_ASSET_REQUEST}
`;

export const CREATE_ASSET_REQUEST: TypedDocumentNode<
  { assetRequestCreate: AssetRequest },
  { input: AssetRequestCreateInput }
> = gql`
  mutation assetRequestCreate($input: AssetRequestCreateInput!) {
    assetRequestCreate(input: $input) {
      ...BaseAssetRequest
    }
  }
  ${BASE_ASSET_REQUEST}
`;

export const UPDATE_ASSET_REQUEST: TypedDocumentNode<
  { assetRequestUpdate: AssetRequest },
  { input: AssetRequestUpdateInput }
> = gql`
  mutation assetRequestUpdate($input: AssetRequestUpdateInput!) {
    assetRequestUpdate(input: $input) {
      ...BaseAssetRequest
    }
  }
  ${BASE_ASSET_REQUEST}
`;

// Unique to SDDOT related tenants
export const CREATE_REPLENISHMENT: TypedDocumentNode<
  { replenishmentCreate: AssetRequest },
  { input: ReplenishmentCreateInput }
> = gql`
  mutation replenishmentCreate($input: ReplenishmentCreateInput!) {
    replenishmentCreate(input: $input) {
      ...BaseAssetRequest
    }
  }
  ${BASE_ASSET_REQUEST}
`;

export const UPDATE_REPLENISHMENT: TypedDocumentNode<
  { replenishmentUpdate: AssetRequest },
  { input: ReplenishmentUpdateInput }
> = gql`
  mutation replenishmentUpdate($input: ReplenishmentUpdateInput!) {
    replenishmentUpdate(input: $input) {
      ...BaseAssetRequest
    }
  }
  ${BASE_ASSET_REQUEST}
`;
